import React from 'react';
import manualBudgetService from '../services/manualBudgetService';

const BudgetTemplatePreview = ({ budgetData }) => {
  if (!budgetData) {
    return (
      <div className="bg-gradient-to-br from-secondary-800 via-secondary-700 to-secondary-800 rounded-3xl p-16 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-secondary-600 rounded-xl w-48 mx-auto"></div>
          <div className="h-4 bg-secondary-600 rounded w-64 mx-auto"></div>
        </div>
      </div>
    );
  }

  const formatCurrency = (value) => manualBudgetService.formatCurrency(value || 0);
  const formatDate = (date) => manualBudgetService.formatDate(date);

  const items = budgetData.items || [];
  const paymentConditions = budgetData.paymentConditions || [];
  const terms = budgetData.terms || [];
  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  const companyInfo = budgetData.companyInfo || {
    name: 'SISTEMA SANTIAGO',
    email: 'moveis@santiago.com.br',
    phone: '(11) 99999-9999'
  };

  return (
    <div className="bg-gradient-to-br from-secondary-50 via-white to-secondary-50 min-h-screen">
      {/* Hero Section - Premium Header */}
      <div className="relative overflow-hidden">
        {/* Solid Background with Subtle Pattern */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600 via-primary-500 to-primary-600">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        {/* Geometric Accent Shapes */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-400/20 clip-path-polygon transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary-700/30 clip-path-polygon transform -translate-x-1/2 translate-y-1/2"></div>

        {/* Subtle Diagonal Line Pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="diagonalLines" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                <line x1="0" y1="0" x2="0" y2="8" stroke="#fff" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#diagonalLines)"/>
          </svg>
        </div>

        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* Logo Container with Glass Effect */}
              <div className="relative">
                <div className="absolute inset-0 bg-white/20 blur-xl rounded-2xl"></div>
                <div className="relative bg-white/15 backdrop-blur-md rounded-2xl p-3 border border-white/25">
                  <img
                    src="/logo.png"
                    alt="Sistema Santiago"
                    className="h-20 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>

              <div className="text-white">
                <h1 className="text-3xl font-black tracking-tight drop-shadow-lg">{companyInfo.name}</h1>
                <p className="text-primary-200 text-sm font-medium tracking-wide">Móveis Planejados</p>
              </div>
            </div>

            {/* Contact Info with Glass Effect */}
            <div className="hidden md:block bg-white/10 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/20">
              <p className="text-white font-medium">{companyInfo.email}</p>
              <p className="text-primary-200 text-sm">{companyInfo.phone}</p>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z" fill="white"/>
          </svg>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="px-8 py-8 -mt-4 relative z-10">
        <div className="max-w-6xl mx-auto space-y-8">

          {/* Budget Info Cards - Floating Design */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="group relative bg-white rounded-2xl shadow-lg shadow-primary-500/5 border border-secondary-100 p-5 hover:shadow-xl hover:shadow-primary-500/10 transition-all duration-300 hover:-translate-y-1">
              <div className="absolute -top-3 left-4 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg shadow-primary-500/30">ORÇAMENTO</div>
              <p className="text-2xl font-black text-gray-900 mt-2">{budgetData.budget_number || 'ORC-0000'}</p>
            </div>

            <div className="group relative bg-white rounded-2xl shadow-lg shadow-primary-500/5 border border-secondary-100 p-5 hover:shadow-xl hover:shadow-primary-500/10 transition-all duration-300 hover:-translate-y-1">
              <div className="absolute -top-3 left-4 bg-secondary-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg shadow-secondary-500/30">DATA</div>
              <p className="text-2xl font-black text-gray-900 mt-2">{formatDate(budgetData.created_at)}</p>
            </div>

            <div className="group relative bg-white rounded-2xl shadow-lg shadow-primary-500/5 border border-secondary-100 p-5 hover:shadow-xl hover:shadow-primary-500/10 transition-all duration-300 hover:-translate-y-1">
              <div className="absolute -top-3 left-4 bg-warning-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg shadow-warning-500/30">VALIDADE</div>
              <p className="text-2xl font-black text-warning-600 mt-2">{budgetData.validity_days || 30} <span className="text-sm font-normal text-gray-500">dias</span></p>
            </div>

            <div className="group relative bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-xl shadow-primary-500/30 p-5 text-white overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full blur-2xl transform translate-x-1/2 -translate-y-1/2"></div>
              <div className="text-xs font-medium text-primary-100 uppercase tracking-wider">VALOR TOTAL</div>
              <p className="text-2xl font-black mt-1 relative z-10">{formatCurrency(budgetData.total_amount || subtotal)}</p>
            </div>
          </div>

          {/* Client & Project Section - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client Card */}
            <div className="relative bg-white rounded-3xl shadow-lg border border-secondary-100 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600"></div>

              {/* Subtle Corner Accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary-50 to-transparent opacity-50"></div>

              <div className="p-6 pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Dados do Cliente</h3>
                    <p className="text-sm text-gray-500">Informações do cliente</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-xl border border-secondary-100">
                    <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Nome</p>
                      <p className="font-bold text-gray-900">{budgetData.client_name || '-'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-xl border border-secondary-100">
                    <div className="w-10 h-10 bg-secondary-100 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Endereço</p>
                      <p className="font-medium text-gray-700 text-sm">{budgetData.client_address || '-'}</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="flex-1 flex items-center gap-3 p-3 bg-secondary-50 rounded-xl border border-secondary-100">
                      <div className="w-10 h-10 bg-success-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Telefone</p>
                        <p className="font-medium text-gray-700 text-sm">{budgetData.client_phone || '-'}</p>
                      </div>
                    </div>
                    <div className="flex-1 flex items-center gap-3 p-3 bg-secondary-50 rounded-xl border border-secondary-100">
                      <div className="w-10 h-10 bg-secondary-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <p className="font-medium text-gray-700 text-sm truncate">{budgetData.client_email || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Description Card */}
            <div className="relative bg-white rounded-3xl shadow-lg border border-secondary-100 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-warning-400 via-warning-500 to-warning-600"></div>

              {/* Subtle Corner Accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-warning-50 to-transparent opacity-50"></div>

              <div className="p-6 pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-warning-500 to-warning-600 rounded-xl flex items-center justify-center shadow-lg shadow-warning-500/20">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Descrição do Projeto</h3>
                    <p className="text-sm text-gray-500">Detalhes do serviço</p>
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-br from-secondary-50 to-secondary-100 rounded-2xl border border-secondary-100">
                  {budgetData.project_description ? (
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{budgetData.project_description}</p>
                  ) : (
                    <p className="text-gray-400 italic">Nenhuma descrição adicionada</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Items Section - Premium Table Design */}
          <div className="relative bg-white rounded-3xl shadow-lg border border-secondary-100 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600"></div>

            <div className="p-6 pt-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Itens do Orçamento</h3>
                    <p className="text-sm text-gray-500">{items.length} {items.length === 1 ? 'item' : 'itens'}</p>
                  </div>
                </div>

                <div className="px-4 py-2 bg-primary-50 rounded-xl border border-primary-200">
                  <span className="text-primary-700 font-bold">Subtotal: {formatCurrency(subtotal)}</span>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-hidden rounded-xl border border-secondary-200">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-primary-500 via-primary-600 to-primary-500 text-white">
                      <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">#</th>
                      <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">Descrição</th>
                      <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider">Qtd</th>
                      <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider">Un</th>
                      <th className="px-4 py-4 text-right text-xs font-bold uppercase tracking-wider">Valor Unit.</th>
                      <th className="px-4 py-4 text-right text-xs font-bold uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center">
                          <div className="flex flex-col items-center">
                            <svg className="w-12 h-12 text-secondary-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                            <p className="text-secondary-400 font-medium">Nenhum item adicionado</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      items.map((item, index) => (
                        <tr key={item.id || index} className={`${index % 2 === 0 ? 'bg-white' : 'bg-secondary-50/50'} hover:bg-primary-50/30 transition-colors`}>
                          <td className="px-4 py-4">
                            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                              <span className="text-primary-700 font-bold text-sm">{index + 1}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <p className="font-semibold text-gray-900">{item.name}</p>
                            {item.description && (
                              <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                            )}
                          </td>
                          <td className="px-4 py-4 text-center font-medium text-gray-700">{item.quantity}</td>
                          <td className="px-4 py-4 text-center">
                            <span className="px-2 py-1 bg-secondary-100 rounded-lg text-xs font-medium text-secondary-600">{item.unit}</span>
                          </td>
                          <td className="px-4 py-4 text-right font-medium text-gray-700">{formatCurrency(item.unitPrice)}</td>
                          <td className="px-4 py-4 text-right">
                            <span className="px-3 py-1 bg-primary-100 rounded-xl text-primary-700 font-bold text-sm">
                              {formatCurrency(item.quantity * item.unitPrice)}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Value Highlight Section - Premium Design */}
          <div className="relative">
            {/* Background Decoration */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary-600 via-primary-500 to-primary-600 rounded-3xl transform rotate-1 scale-105 opacity-20"></div>

            <div className="relative bg-gradient-to-br from-primary-600 via-primary-500 to-primary-600 rounded-3xl p-8 shadow-2xl shadow-primary-500/30 overflow-hidden">
              {/* Geometric Accent */}
              <div className="absolute top-0 right-0 w-96 h-96 bg-primary-400/20 clip-path-polygon transform translate-x-1/3 -translate-y-1/3"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary-700/30 clip-path-polygon transform -translate-x-1/3 translate-y-1/3"></div>

              {/* Subtle Pattern Overlay */}
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='3' cy='3' r='1'/%3E%3C/g%3E%3C/svg%3E")`
              }}></div>

              <div className="relative text-center text-white z-10">
                <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-white/20">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium uppercase tracking-wider">Valor Total do Orçamento</span>
                </div>

                <div className="space-y-2">
                  <p className="text-6xl md:text-7xl font-black tracking-tight drop-shadow-lg">
                    {formatCurrency(budgetData.total_amount || subtotal)}
                  </p>

                  {budgetData.discount && budgetData.discount.type !== 'NONE' && budgetData.discount.value > 0 && (
                    <div className="inline-flex items-center gap-2 bg-warning-400 text-warning-900 px-4 py-2 rounded-full mt-4 shadow-lg shadow-warning-400/30">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5a2 2 0 10-2 2h-2a2 2 0 10-2 2v14l6-3-6-3z" />
                      </svg>
                      <span className="font-bold">
                        {budgetData.discount.type === 'PERCENT'
                          ? `${budgetData.discount.value}% desconto aplicado`
                          : `Desconto de ${formatCurrency(budgetData.discount.value)} aplicado`
                        }
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Conditions - Modern Cards */}
          {paymentConditions.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Condições de Pagamento</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {paymentConditions.map((condition, index) => {
                  const isVista = condition.type === 'VISTA';
                  const isEntrada = condition.type === 'ENTRADA_PARCELADO';
                  const installments = condition.installments || 1;
                  const downPayment = condition.downPaymentValue || 0;
                  const showPerParcel = !isVista;
                  const entryAmount = isEntrada ? downPayment : 0;
                  const remainingAmount = isEntrada ? (condition.installmentAmount || 0) : (condition.installmentAmount || 0);

                  return (
                    <div
                      key={index}
                      className={`relative group rounded-2xl p-6 transition-all duration-300 hover:scale-105 ${
                        isVista
                          ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-xl shadow-primary-500/30'
                          : isEntrada
                          ? 'bg-gradient-to-br from-amber-50 to-orange-100 border-2 border-amber-200 hover:border-amber-400 hover:shadow-xl hover:shadow-amber-500/10'
                          : 'bg-white border-2 border-secondary-100 hover:border-primary-300 hover:shadow-xl hover:shadow-primary-500/10'
                      }`}
                    >
                      {isVista && (
                        <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full blur-xl transform translate-x-1/2 -translate-y-1/2"></div>
                      )}

                      <div className={`absolute -top-3 left-4 px-3 py-1 rounded-full text-xs font-bold uppercase shadow-lg ${
                        isVista
                          ? 'bg-warning-400 text-warning-900'
                          : isEntrada
                          ? 'bg-amber-400 text-amber-900'
                          : 'bg-secondary-100 text-secondary-600'
                      }`}>
                        {isVista ? '★ Destaque' : isEntrada ? `${installments}x entrada` : `${installments}x`}
                      </div>

                      <div className="mt-4">
                        <p className={`text-sm font-medium mb-2 ${
                          isVista ? 'text-primary-100' : isEntrada ? 'text-amber-700' : 'text-gray-500'
                        }`}>
                          {isVista
                            ? 'Pagamento à Vista'
                            : isEntrada
                            ? `Entrada de ${formatCurrency(entryAmount)} + ${installments - 1}x`
                            : `Em ${installments}x sem juros`
                          }
                        </p>

                        {isEntrada ? (
                          <div className="space-y-3">
                            <div className="flex items-baseline gap-2">
                              <span className="text-sm text-amber-600 font-medium">Entrada:</span>
                              <span className="text-2xl font-black text-amber-700">{formatCurrency(entryAmount)}</span>
                            </div>
                            <div className="flex items-baseline gap-2">
                              <span className="text-sm text-amber-600 font-medium">+ {installments - 1}x de:</span>
                              <span className="text-2xl font-black text-amber-700">{formatCurrency(remainingAmount)}</span>
                            </div>
                            <p className="text-xs text-amber-600">por parcela (após entrada)</p>
                          </div>
                        ) : (
                          <>
                            <p className={`text-3xl font-black ${isVista ? 'text-white' : 'text-gray-900'}`}>
                              {formatCurrency(condition.installmentAmount || condition.totalAmount)}
                            </p>
                            {showPerParcel && (
                              <p className="text-sm text-gray-500 mt-1">por parcela</p>
                            )}
                          </>
                        )}

                        {(condition.interestRate || 0) > 0 && (
                          <div className={`mt-3 inline-flex items-center gap-1 ${
                            isVista ? 'bg-white/20' : 'bg-amber-50'
                          } px-2 py-1 rounded-lg`}>
                            <svg className={`w-4 h-4 ${isVista ? 'text-warning-200' : 'text-amber-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className={`text-xs font-medium ${isVista ? 'text-white' : 'text-amber-600'}`}>
                              Taxa de {condition.interestRate}% a.m.
                            </span>
                          </div>
                        )}

                        {condition.description && (
                          <p className={`text-sm mt-3 ${isVista ? 'text-primary-100' : isEntrada ? 'text-amber-600' : 'text-gray-500'}`}>
                            {condition.description}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Terms Section - Premium Design with Footer Style */}
          <div className="relative bg-gradient-to-br from-secondary-800 via-secondary-700 to-secondary-800 rounded-3xl shadow-xl overflow-hidden">
            {/* Top Accent Line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600"></div>

            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl"></div>

            {/* Subtle Pattern */}
            <div className="absolute inset-0 opacity-5" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M20 0v40M0 20h40'/%3E%3C/g%3E%3C/svg%3E")`
            }}></div>

            <div className="relative p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Termos e Condições</h3>
                  <p className="text-sm text-secondary-400">Condições gerais deste orçamento</p>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
                <div className="space-y-3">
                  {terms.length === 0 ? (
                    <p className="text-secondary-400 italic">Nenhum termo adicionado</p>
                  ) : (
                    terms.map((term, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm shadow-emerald-500/30">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="flex-1 text-secondary-200 font-medium">{term.term_text || term}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-center gap-3">
                <svg className="w-5 h-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-secondary-300 text-sm">Gerado em {new Date().toLocaleString('pt-BR')} - {companyInfo.name}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BudgetTemplatePreview;
